import React, { ChangeEvent, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearDocumentsError, searchContent, sortContent, switchView } from '../../../store/actions/admin/content-actions';
import { ICONS } from '../../general-ui/icon';
import IconToggle from '../../general-ui/icon-toggle/icon-toggle';
import { FILE_TYPES, SORT_METHODS } from '../../../store/utils/content-utils';
import EditingOptions from './editing-options';
import UploadMenu from './upload-menu';
import { EventsState } from '../../../store/types';
import Search from '../../general-ui/search-bar/search';
import SmallSelect from '../../general-ui/select/small-select';
import './content-header.scss';
import { showAlert } from '../../general-ui/alert/alert-service';
import { clearDeleteVideoError } from '../../../store/actions/admin/videos';

const sortByOptions = [
	{
		label: "Newest",
		value: SORT_METHODS.NEWEST
	},
	{
		label: "Oldest",
		value: SORT_METHODS.OLDEST
	},
	{
		label: "A-Z",
		value: SORT_METHODS.AZ
	},
	{
		label: "Z-A",
		value: SORT_METHODS.ZA
	},
	{
		label: "Smallest",
		value: SORT_METHODS.SMALLEST
	},
	{
		label: "Largest",
		value: SORT_METHODS.LARGEST
	},
	{
		label: "Shortest",
		value: SORT_METHODS.SHORTEST
	},
	{
		label: "Longest",
		value: SORT_METHODS.LONGEST
	}
];
interface ContentHeaderProps {
	idList: (number | string)[]
}

const ContentHeader: React.FC<ContentHeaderProps> = ({ idList }) => {
	const {
		ContentReducer: {
			checkedSet,
			view,
			sortBy,
			fileType,
			documentsError
		},
		CreateEventReducer: {
			colorPacks,
			fontPacks
		},
		VideosReducer: {
			deleteVideoError
		}
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();
	const checkedSetSize = checkedSet.size;
	const isEditing = checkedSetSize > 0;

	useEffect(() => {
		if (documentsError || deleteVideoError) {

			let list = "";
			if (deleteVideoError) {
				list = deleteVideoError;
			} else if (typeof documentsError === "string") {
				list = documentsError;
			} else if (documentsError?.contentType) {
				const { contentType, cannotDelete } = documentsError;

				switch (contentType) {
					case (FILE_TYPES.PALETTES): {
						list = colorPacks
							.filter(color => cannotDelete.includes(color.color_pack))
							.map(color => color.title)
							.join(", ");
						break;
					}
					case (FILE_TYPES.FONTS): {
						list = fontPacks
							.filter(font => cannotDelete.includes(font.font_pack))
							.map(font => font.title)
							.join(", ");
						break;
					}
				}
			}
			showAlert({
				message: "Could not delete",
				description: "The following content must be removed from all events before being deleted: " + list,
				duration: 4000,
				type: "error"
			});
			dispatch(clearDocumentsError());
			dispatch(clearDeleteVideoError());
		}
	}, [documentsError, dispatch, colorPacks, fontPacks, deleteVideoError]);

	const handleSortByChange = (value: string) => dispatch(sortContent(value));

	const handleViewToggle = ({ target }: ChangeEvent<HTMLInputElement>) => dispatch(switchView(target.value as "grid" | "list"));

	const handleSearch = (search: string) => dispatch(searchContent(search));
	const handleClear = () => dispatch(searchContent(""));

	const filteredOptions = useMemo(() => sortByOptions.filter(({ value }) => {
		const videoOptions = value !== SORT_METHODS.LARGEST && value !== SORT_METHODS.SMALLEST;
		const docOptions = value !== SORT_METHODS.LONGEST && value !== SORT_METHODS.SHORTEST;
		const miscOptions = videoOptions && docOptions;
		switch (fileType) {
			case (FILE_TYPES.UPLOADED):
			case (FILE_TYPES.RECORDED): {
				if (videoOptions) { return true; }
				else { return false; }
			}
			case (FILE_TYPES.FONTS): {
				if (miscOptions) { return true; }
				else { return false; }
			}
			case (FILE_TYPES.PALETTES): {
				if (miscOptions) { return true; }
				else { return false; }
			}
			// Documents and Images
			default: {
				if (docOptions) { return true; }
				else { return false; }
			}
		}
	}), [fileType]);

	return (
		<div className="content-header">
			<h1 className="content-title">Content</h1>
			{isEditing
				? <EditingOptions checkedSetSize={checkedSetSize} fileType={fileType} idList={idList} />
				: (
					<div className="content-options">
						<Search onClear={handleClear} onSearch={handleSearch} />

						<SmallSelect
							placeholder="Sort By"
							options={filteredOptions}
							onChange={handleSortByChange}
							selected={sortBy}
						/>

						<IconToggle
							name="view toggle"
							leftValue="list"
							leftIconName={ICONS.LAYOUT_LIST}
							rightValue="grid"
							rightIconName={ICONS.LAYOUT_GRID}
							onToggle={handleViewToggle}
							selected={view}
						/>

						<UploadMenu />
					</div>
				)
			}
		</div>
	);
};

export default ContentHeader;
